import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

import localUtil from '../lib/util';

configure({ enforceActions: 'always' });

class RaceClassContestantStore extends LocalModel {
    constructor() {
        super('raceClassContestant', {
            namePlural: 'raceClassContestants',
            namePluralReal: 'raceClassContestants',
            sort: 'startTime bib signupDate id',
            // sort: '-signupDate id',
            limit: 500,
            api: {
                search: {
                    url: '/api/raceclasscontestants/',
                    params: {
                        limit: 500,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/raceclasscontestants/',
                    params: {},
                },
                save: {
                    url: '/api/raceclasscontestants/',
                    params: {},
                },
                delete: {
                    url: '/api/raceclasscontestants/',
                    params: {},
                },
            },
        });
    }

    @observable hasSignedUp = false;

    @observable hasSignedUpToRace = false;

    @observable newRaceClassContestant = {};

    @observable raceClassContestant = {};

    @observable raceClassContestants = [];

    @observable adminRaceClasses = [];

    @observable raceClasses = [];

    @observable moveTargetRaceClasses = [];

    @observable raceContestants = [];

    @observable adminRaceClassContestants = [];

    @observable raceContestant = {};

    @observable countries = {};

    @observable countriesTotal = {};

    @observable showOfflineResults = false;

    @observable showOnlyAutoResults = false;

    async importCsvPreview(raceClassId, csv, columnMapping = {}) {
        return util.fetchApi(
            `/api/raceclasscontestants/csv/import-preview/${raceClassId}`,
            { method: 'POST' },
            { csv, columnMapping },
        );
    }

    async importCsvCommit(raceClassId, csv, columnMapping = {}, selectedRows = [], importToken) {
        return util.fetchApi(
            `/api/raceclasscontestants/csv/import/${raceClassId}`,
            { method: 'POST' },
            {
                csv, columnMapping, selectedRows, importToken,
            },
        );
    }

    // Classes an admin can move a contestant into, with live contestant
    // totals so free seats can be shown. Kept separate from `raceClasses`
    // so the signup list under the drawer is never overwritten.
    async loadMoveTargets(race) {
        const response = await util.fetchApi('/api/raceclasses/', { method: 'GET' }, {
            race,
            addData: 'totalContestants',
            limit: 100,
        });
        if (response.status === 200) {
            this.updateKeyValue('moveTargetRaceClasses', response.data || []);
        }
        return response;
    }

    async moveToRaceClass(id, raceClass) {
        const response = await util.fetchApi(
            `/api/raceclasscontestants/admin/${id}/move`,
            { method: 'POST' },
            { raceClass },
        );
        if (response.status === 200 && response.data?.raceClassContestant) {
            this.updateKeyValue('raceClassContestant', response.data.raceClassContestant);
        }
        return response;
    }

    @action
    getCountries() {
        this.countries = {};
        this.countriesTotal = {};
        this.raceClassContestants.forEach(rcc => {
            if (!this.countries[rcc.raceClass]) {
                this.countries[rcc.raceClass] = {};
            }
            const country = localUtil.resolveCountry(rcc.country);
            if (!this.countries[rcc.raceClass][country]) {
                this.countries[rcc.raceClass][country] = 0;
            }
            this.countries[rcc.raceClass][country] += 1;

            if (!country) { return; }
            if (!this.countriesTotal[country]) {
                this.countriesTotal[country] = 0;
            }
            this.countriesTotal[country] += 1;
        });
    }

    async loadContestants({
        race,
        raceClass,
        user,
        emptyFirst = true,
        includeLoadStatus = false,
    }) {
        if (emptyFirst) {
            this.updateKeyValue('raceClassContestant', {});
            this.updateKeyValue('hasSignedUp', false);
            this.updateKeyValue('hasSignedUpToRace', false);
        }

        const query = {
            race,
            raceClass,
            user,
            // cellphone: encodeURIComponent(cellphone),
        };
        const response = await this.load({ query, skipUpdate: true,  addData: ['raceContestants'] });
        if (Array.isArray(this.raceContestants)) {
            const raceContestant = this.raceContestants[0];
            this.updateKeyValue('raceContestant', raceContestant);
            if ((raceContestant && raceContestant.user === user)) {
                this.updateKeyValue('hasSignedUpToRace', true);
            }
        }
        if (Array.isArray(response) && response[0]) {
            const contestant = {
                ...response[0],
                paymentReservationClientLoadedAt: Date.now(),
            };
            this.updateKeyValue('raceClassContestant', contestant);
            if ((contestant && contestant.user === user)) {
                this.updateKeyValue('hasSignedUp', true);
            }

            // Check for other raceClass signups
            return includeLoadStatus ? { loaded: true, contestant } : contestant;
        }
        if (Array.isArray(response)) {
            this.updateKeyValue('raceClassContestant', {});
            this.updateKeyValue('hasSignedUp', false);
        }
        if (includeLoadStatus) {
            return {
                loaded: Array.isArray(response),
                contestant: null,
            };
        }
        return null;
    }

    // - - - - [ Signup Fee ] - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    async paySignupFeeByVipps(uuidv4) {
        const raceClassContestant = this.raceClassContestant;
        const response = await util.fetchApi('/api/vipps/request-payment', { method: 'POST' }, {
            raceClassContestant: raceClassContestant.id,
            type: 'signupfee',
            amount: raceClassContestant.signupfeeAmount,
            title: `Race signup fee - ${raceClassContestant.name}`,
            customer: raceClassContestant.customer,
            wantedPage: window.location.href,
        });
        switch (response.status) {
            case 200:
                this.updateObjectKeyValue('raceClassContestant', 'vippsSignupfeeReference', response.data.reference);
                this.updateObjectKeyValue('raceClassContestant', 'vippsSignupfeeRedirectUrl', response.data.redirectUrl);
                return true;
                break;
            default:
                return false;
                break;
        }
    }

    async checkSignupFeeByVipps(uuidv4) {
        const response = await this.load({ query: { uuidv4 }, skipUpdate: true });
        if (Array.isArray(response) && response[0]) {
            const contestant = response[0];
            if (contestant.paidSignupfee) {
                this.updateObjectKeyValue('raceClassContestant', 'paidSignupfee', contestant.paidSignupfee);
                return true;
            }
        }
        return false;
    }

    async captureSignupFeeByVipps(uuidv4) {
        // Capture is now automatic in the backend - this method is kept for backwards compatibility
        return await this.checkSignupFeeByVipps(uuidv4);
    }

    async cancelSignupFeeByVipps(uuidv4) {
        // Cancel functionality not implemented for generic endpoints
        // Payment will expire automatically if not completed
        this.updateObjectKeyValue('raceClassContestant', 'vippsSignupfeeReference', null);
        this.updateObjectKeyValue('raceClassContestant', 'vippsSignupfeeRedirectUrl', null);
        return true;
    }

    // - - - - [ Signup ] - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    async paySignupByVipps(uuidv4) {
        const raceClassContestant = this.raceClassContestant;
        const response = await util.fetchApi('/api/vipps/request-payment', { method: 'POST' }, {
            raceClassContestant: raceClassContestant.id,
            type: 'signup',
            amount: raceClassContestant.signuppaymentAmount,
            title: `Race signup - ${raceClassContestant.name}`,
            customer: raceClassContestant.customer,
            wantedPage: window.location.href,
        });
        switch (response.status) {
            case 200:
                this.updateObjectKeyValue('raceClassContestant', 'vippsSignupReference', response.data.reference);
                this.updateObjectKeyValue('raceClassContestant', 'vippsSignupRedirectUrl', response.data.redirectUrl);
                return true;
                break;
            default:
                return false;
                break;
        }
    }

    async checkSignupByVipps(uuidv4) {
        const response = await this.load({ query: { uuidv4 }, skipUpdate: true });
        if (Array.isArray(response) && response[0]) {
            const contestant = response[0];
            if (contestant.paidSignup) {
                this.updateObjectKeyValue('raceClassContestant', 'paidSignup', contestant.paidSignup);
                return true;
            }
        }
        return false;
    }

    async captureSignupByVipps(uuidv4) {
        // Capture is now automatic in the backend - this method is kept for backwards compatibility
        return await this.checkSignupByVipps(uuidv4);
    }

    async cancelSignupByVipps(uuidv4) {
        // Cancel functionality not implemented for generic endpoints
        // Payment will expire automatically if not completed
        this.updateObjectKeyValue('raceClassContestant', 'vippsSignupReference', null);
        this.updateObjectKeyValue('raceClassContestant', 'vippsSignupRedirectUrl', null);
        return true;
    }

    // - - - - [ Rest ] - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    async payRestByVipps(uuidv4) {
        const raceClassContestant = this.raceClassContestant;
        const response = await util.fetchApi('/api/vipps/request-payment', { method: 'POST' }, {
            raceClassContestant: raceClassContestant.id,
            type: 'rest',
            amount: raceClassContestant.restPaymentAmount,
            title: `Race rest payment - ${raceClassContestant.name}`,
            customer: raceClassContestant.customer,
            wantedPage: window.location.href,
        });
        switch (response.status) {
            case 200:
                this.updateObjectKeyValue('raceClassContestant', 'vippsRestReference', response.data.reference);
                this.updateObjectKeyValue('raceClassContestant', 'vippsRestRedirectUrl', response.data.redirectUrl);
                return true;
                break;
            default:
                return false;
                break;
        }
    }

    async checkRestByVipps(uuidv4) {
        const response = await this.load({ query: { uuidv4 }, skipUpdate: true });
        if (Array.isArray(response) && response[0]) {
            const contestant = response[0];
            if (contestant.paidRest) {
                this.updateObjectKeyValue('raceClassContestant', 'paidRest', contestant.paidRest);
                return true;
            }
        }
        return false;
    }

    async captureRestByVipps(uuidv4) {
        // Capture is now automatic in the backend - this method is kept for backwards compatibility
        return await this.checkRestByVipps(uuidv4);
    }

    async cancelRestByVipps(uuidv4) {
        // Cancel functionality not implemented for generic endpoints
        // Payment will expire automatically if not completed
        this.updateObjectKeyValue('raceClassContestant', 'vippsRestReference', null);
        this.updateObjectKeyValue('raceClassContestant', 'vippsRestRedirectUrl', null);
        return true;
    }

    // - - - - [ PayPal - Signup Fee ] - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    async paySignupFeeByPaypal(uuidv4) {
        const raceClassContestant = this.raceClassContestant;
        const response = await util.fetchApi('/api/paypal/order-create', { method: 'POST' }, {
            raceClassContestant: raceClassContestant.id,
            type: 'signupfee',
            amount: raceClassContestant.signupfeeAmount,
            title: `Race signup fee - ${raceClassContestant.name}`,
            customer: raceClassContestant.customer,
            wantedPage: window.location.href,
        });
        switch (response.status) {
            case 200:
                this.updateObjectKeyValue('raceClassContestant', 'paypalSignupfeeReference', response.data.id);
                this.updateObjectKeyValue('raceClassContestant', 'paypalSignupfeeObject', response.data);
                if (response.data.redirectUrl) {
                    window.open(response.data.redirectUrl, '_blank');
                }
                return true;
                break;
            default:
                return false;
                break;
        }
    }

    async checkSignupFeeByPaypal(uuidv4) {
        const response = await this.load({ query: { uuidv4 }, skipUpdate: true });
        if (Array.isArray(response) && response[0]) {
            const contestant = response[0];
            if (contestant.paidSignupfee) {
                this.updateObjectKeyValue('raceClassContestant', 'paidSignupfee', contestant.paidSignupfee);
                return true;
            }
        }
        return false;
    }

    // - - - - [ PayPal - Signup ] - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    async paySignupByPaypal(uuidv4) {
        const raceClassContestant = this.raceClassContestant;
        const response = await util.fetchApi('/api/paypal/order-create', { method: 'POST' }, {
            raceClassContestant: raceClassContestant.id,
            type: 'signup',
            amount: raceClassContestant.signuppaymentAmount,
            title: `Race signup - ${raceClassContestant.name}`,
            customer: raceClassContestant.customer,
            wantedPage: window.location.href,
        });
        switch (response.status) {
            case 200:
                this.updateObjectKeyValue('raceClassContestant', 'paypalSignupReference', response.data.id);
                this.updateObjectKeyValue('raceClassContestant', 'paypalSignupObject', response.data);
                if (response.data.redirectUrl) {
                    window.open(response.data.redirectUrl, '_blank');
                }
                return true;
                break;
            default:
                return false;
                break;
        }
    }

    async checkSignupByPaypal(uuidv4) {
        const response = await this.load({ query: { uuidv4 }, skipUpdate: true });
        if (Array.isArray(response) && response[0]) {
            const contestant = response[0];
            if (contestant.paidSignup) {
                this.updateObjectKeyValue('raceClassContestant', 'paidSignup', contestant.paidSignup);
                return true;
            }
        }
        return false;
    }

    // - - - - [ PayPal - Rest ] - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    async payRestByPaypal(uuidv4) {
        const raceClassContestant = this.raceClassContestant;
        const response = await util.fetchApi('/api/paypal/order-create', { method: 'POST' }, {
            raceClassContestant: raceClassContestant.id,
            type: 'rest',
            amount: raceClassContestant.restPaymentAmount,
            title: `Race rest payment - ${raceClassContestant.name}`,
            customer: raceClassContestant.customer,
            wantedPage: window.location.href,
        });
        switch (response.status) {
            case 200:
                this.updateObjectKeyValue('raceClassContestant', 'paypalRestReference', response.data.id);
                this.updateObjectKeyValue('raceClassContestant', 'paypalRestObject', response.data);
                if (response.data.redirectUrl) {
                    window.open(response.data.redirectUrl, '_blank');
                }
                return true;
                break;
            default:
                return false;
                break;
        }
    }

    async checkRestByPaypal(uuidv4) {
        const response = await this.load({ query: { uuidv4 }, skipUpdate: true });
        if (Array.isArray(response) && response[0]) {
            const contestant = response[0];
            if (contestant.paidRest) {
                this.updateObjectKeyValue('raceClassContestant', 'paidRest', contestant.paidRest);
                return true;
            }
        }
        return false;
    }

    async loadPublic(opt = { query: {} }) {
        const response = await util.fetchApi(`/api/raceclasscontestants/public`, {  method: 'GET' }, { ...opt.query, limit: opt.limit || 1000 });
        switch (response.status) {
            case 200:
                this.updateKeyValue('raceClassContestants', response.data);
                return true;
                break;
            default:
                return false;
                break;
        }
    }

    async loadWithResults(opt = { query: {} }) {
        // Uses authenticated endpoint that supports includeOffline parameter
        const response = await util.fetchApi(`/api/raceclasscontestants/`, {  method: 'GET' }, { ...opt.query, limit: opt.limit || 1000 });
        switch (response.status) {
            case 200:
                this.updateKeyValue('raceClassContestants', response.data);
                return true;
                break;
            default:
                return false;
                break;
        }
    }

    @action
    calculateTimeUsed(sortedCheckpoints, raceClassId) {
        // const timeused = timeUsed(rcc, sortedCheckpoints, rcc);
        for (let i = 0, l = this.raceClassContestants.length; i < l; i += 1) {
            const rcc = this.raceClassContestants[i];
            if (rcc.raceClass !== raceClassId) {
                continue;
            }
            rcc.raceTimeused = 0;

            const startTime = rcc.startTime;
            let lastTimestamp = startTime;
            let prevRccCp = null;
            sortedCheckpoints.forEach(cp => {
                const rccCp = rcc.checkpoints?.find(e => e.checkpoint === cp.md5) || {};
                if (rccCp.timestampOut || rccCp.timestampIn) {
                    lastTimestamp = rccCp.timestampOut || rccCp.timestampIn;
                }
                if (prevRccCp && prevRccCp.timestampOut && rccCp.timestampIn) {
                    const timeused = util.dateDiff(prevRccCp.timestampOut, rccCp.timestampIn);
                    rcc.raceTimeused += timeused.seconds;
                }
                prevRccCp = rccCp;
            });

            if (startTime === lastTimestamp) {
                rcc.timeused = 0;
                continue;
            }

            const tu = util.dateDiff(startTime, lastTimestamp)
        // console.log({ startTime, lastTimestamp, seconds: tu.seconds });
            rcc.timeused = tu.seconds;
            rcc.startTime = startTime;
            rcc.endTime = lastTimestamp;
        }
    }
}

const store = new RaceClassContestantStore();
export default store;
